import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Global chat-notifications hook. Poll /team-chat/channels every 10 s while
 * the user is logged in and expose:
 *   • `totalUnread` — sum across all channels (used for the sidebar badge)
 *   • `channels`    — raw channel list (unused externally; kept for future
 *                     use like per-channel badges from anywhere)
 *
 * Side effect: when the unread count for a channel INCREASES between
 * polls, fires a native browser Notification (if the user granted
 * permission via `Notification.requestPermission()`).
 *
 * We intentionally do NOT fire notifications for the channel the user is
 * currently viewing — that would be spammy.
 */
const POLL_MS = 10000;

export function useChatNotifications() {
  const { user } = useAuth();
  const location = useLocation();
  const [channels, setChannels] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const previousUnreadRef = useRef({});  // { channelId: count }
  const previousLastMsgRef = useRef({}); // { channelId: last_message_at }

  useEffect(() => {
    if (!user) {
      setChannels([]);
      setTotalUnread(0);
      previousUnreadRef.current = {};
      previousLastMsgRef.current = {};
      return undefined;
    }

    let cancelled = false;
    let iv;

    const tick = async () => {
      try {
        const { data } = await api.get('/team-chat/channels');
        if (cancelled) return;

        // Fire desktop notifications for any channel that gained new
        // messages, except the one the user is actively viewing.
        const onChatPage = location.pathname.startsWith('/team-chat');
        const canNotify = typeof Notification !== 'undefined' && Notification.permission === 'granted';
        if (canNotify) {
          data.forEach((c) => {
            const prev = previousUnreadRef.current[c.id] ?? 0;
            const prevLast = previousLastMsgRef.current[c.id];
            const hasNew = c.unread_count > prev && c.last_message_at && c.last_message_at !== prevLast;
            // Skip on first poll (no baseline yet) and skip the active channel
            const isFirstPoll = !(c.id in previousUnreadRef.current);
            if (hasNew && !isFirstPoll && !onChatPage) {
              try {
                const n = new Notification(`New message in ${c.name}`, {
                  body: c.last_message_preview || '',
                  icon: '/logo.png',
                  tag: `propveiw-chat-${c.id}`,
                });
                n.onclick = () => {
                  window.focus();
                  window.location.href = '/team-chat';
                };
              } catch (err) {
                console.warn('Notification failed:', err);
              }
            }
          });
        }

        // Update refs + state
        const nextUnread = {};
        const nextLast = {};
        let total = 0;
        data.forEach((c) => {
          nextUnread[c.id] = c.unread_count;
          nextLast[c.id] = c.last_message_at;
          total += c.unread_count;
        });
        previousUnreadRef.current = nextUnread;
        previousLastMsgRef.current = nextLast;
        setChannels(data);
        setTotalUnread(total);
      } catch (err) {
        console.debug('Chat notifications poll failed', err);
      }
    };

    tick();
    iv = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, location.pathname]);

  return { channels, totalUnread };
}
